/**
 * AddStudentsModal — admin-only. Enrolls students into a class via two
 * modes:
 *
 *   "Pick existing"  pick from the school-wide roster (already-enrolled
 *                    students are filtered out). Multi-select + search.
 *   "Create new"     inline form to create a brand-new school_students
 *                    row and enroll them in the same submit.
 *
 * Pick is the common path (most adds are moving an existing student
 * between this term's subject classes). Create is the escape hatch for
 * transfers / mid-term arrivals before a full admissions interview.
 *
 * Backend endpoints used:
 *   POST /api/school/students                — create
 *   POST /api/school/classes/{id}/students   — bulk enroll (idempotent)
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useCreateStudent,
  useEnrollStudents,
  useSchoolStudents,
  type Student,
} from "../../api/school";

interface Props {
  classId: string;
  schoolId: string;
  /** Defaults for the inline-create form so the new student doesn't
   *  arrive in a different grade/section than the class. */
  classGrade: string;
  classSection: string | null;
  /** UUIDs of students already in the class — filtered out of the picker. */
  alreadyEnrolled: ReadonlySet<string>;
  onClose: () => void;
}

type Mode = "pick" | "create";

export function AddStudentsModal({
  classId,
  schoolId,
  classGrade,
  classSection,
  alreadyEnrolled,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>("pick");

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 pt-4 pb-0 border-b border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-slate-800">{t("classes.addStudents.title")}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-xl leading-none"
            >
              ×
            </button>
          </div>
          <div className="flex gap-1 -mb-px">
            <TabButton active={mode === "pick"} onClick={() => setMode("pick")}>
              {t("classes.addStudents.tabPick")}
            </TabButton>
            <TabButton active={mode === "create"} onClick={() => setMode("create")}>
              {t("classes.addStudents.tabCreate")}
            </TabButton>
          </div>
        </header>

        {mode === "pick" ? (
          <PickExisting
            classId={classId}
            schoolId={schoolId}
            alreadyEnrolled={alreadyEnrolled}
            onClose={onClose}
          />
        ) : (
          <CreateAndEnroll
            classId={classId}
            schoolId={schoolId}
            defaultGrade={classGrade}
            defaultSection={classSection}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-sm border-b-2 -mb-px transition ${
        active
          ? "border-sky-500 text-sky-600 font-medium"
          : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

// ── Pick-existing mode ─────────────────────────────────────────────

function PickExisting({
  classId,
  schoolId,
  alreadyEnrolled,
  onClose,
}: {
  classId: string;
  schoolId: string;
  alreadyEnrolled: ReadonlySet<string>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const students = useSchoolStudents(schoolId);
  const enroll = useEnrollStudents(classId);

  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [err, setErr] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const all = students.data ?? [];
    const q = search.trim().toLowerCase();
    return all
      // Status filter: only students past the admissions funnel are
      // eligible to enroll. The funnel statuses
      // (candidate/applicant/interviewed) are in-progress applications
      // and shouldn't show up in the picker — Charles flagged that
      // selecting one and clicking Submit silently enrolls a candidate
      // that hasn't been accepted yet. Terminal statuses
      // (rejected/withdrawn/graduated) are also excluded so old rows
      // don't pollute the list. Migration 017 documents the lifecycle.
      .filter((s) => s.status === "accepted" || s.status === "active")
      .filter((s) => !alreadyEnrolled.has(s.id))
      .filter((s) => {
        if (!q) return true;
        return (
          s.name_zh.toLowerCase().includes(q) ||
          (s.name_en ?? "").toLowerCase().includes(q) ||
          (s.student_code ?? "").toLowerCase().includes(q)
        );
      });
  }, [students.data, alreadyEnrolled, search]);

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit() {
    if (picked.size === 0) {
      setErr(t("classes.addStudents.errAtLeastOne"));
      return;
    }
    setErr(null);
    try {
      await enroll.mutateAsync([...picked]);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <>
      <div className="px-6 pt-4 pb-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("classes.addStudents.searchPlaceholder")}
          className="w-full px-3 py-1.5 text-sm rounded border border-slate-200"
        />
        <p className="text-xs text-slate-500 mt-2">
          {t("classes.addStudents.pickedAndAvailable", { picked: picked.size, available: candidates.length })}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-2">
        {students.isLoading && (
          <p className="text-sm text-slate-500 italic">{t("common.loading")}</p>
        )}
        {!students.isLoading && candidates.length === 0 && (
          <p className="text-sm text-slate-500 italic">
            {search
              ? t("classes.addStudents.noCandidatesQuery")
              : t("classes.addStudents.noCandidates")}
          </p>
        )}
        <ul className="divide-y divide-slate-100">
          {candidates.map((s: Student) => (
            <li key={s.id}>
              <label className="flex items-center gap-3 py-2 hover:bg-slate-50 cursor-pointer px-2 rounded">
                <input
                  type="checkbox"
                  checked={picked.has(s.id)}
                  onChange={() => toggle(s.id)}
                />
                <span className="text-sm text-slate-800 flex-1">
                  {s.name_zh}
                  {s.name_en && (
                    <span className="text-slate-500 text-xs ml-1">
                      ({s.name_en})
                    </span>
                  )}
                </span>
                <span className="text-xs font-mono text-slate-400">
                  {s.student_code ?? "—"}
                </span>
                <span className="text-xs text-slate-500 w-16 text-right">
                  {s.grade}
                  {s.section ?? ""}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>

      {err && <div className="px-6 pt-2 text-xs text-red-600">{err}</div>}

      <footer className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={enroll.isPending}
          className="px-3 py-1.5 text-sm text-slate-600 rounded hover:bg-slate-100 disabled:opacity-50"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={enroll.isPending || picked.size === 0}
          className="px-4 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
        >
          {enroll.isPending
            ? t("classes.addStudents.submitting")
            : t("classes.addStudents.submit", { count: picked.size })}
        </button>
      </footer>
    </>
  );
}

// ── Create-and-enroll mode ─────────────────────────────────────────

function CreateAndEnroll({
  classId,
  schoolId,
  defaultGrade,
  defaultSection,
  onClose,
}: {
  classId: string;
  schoolId: string;
  defaultGrade: string;
  defaultSection: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const createStudent = useCreateStudent();
  const enroll = useEnrollStudents(classId);

  const [nameZh, setNameZh] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [studentCode, setStudentCode] = useState("");
  const [grade, setGrade] = useState(defaultGrade);
  const [section, setSection] = useState(defaultSection ?? "");
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!nameZh.trim()) {
      setErr(t("classes.addStudents.errCreateName"));
      return;
    }
    if (!grade.trim()) {
      setErr(t("classes.addStudents.errCreateGrade"));
      return;
    }
    setErr(null);
    try {
      const created = await createStudent.mutateAsync({
        school_id: schoolId,
        name_zh: nameZh.trim(),
        name_en: nameEn.trim() || null,
        grade: grade.trim(),
        section: section.trim() || null,
        student_code: studentCode.trim() || null,
      });
      await enroll.mutateAsync([created.id]);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const busy = createStudent.isPending || enroll.isPending;

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        <p className="text-xs text-slate-500">
          {t("classes.addStudents.createDescription")}
        </p>

        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="block text-xs text-slate-600 mb-1">
              {t("classes.addStudents.createNameZh")} <span className="text-red-500">*</span>
            </span>
            <input
              type="text"
              value={nameZh}
              onChange={(e) => setNameZh(e.target.value)}
              placeholder={t("classes.addStudents.createNameZhPlaceholder")}
              className="w-full px-2 py-1.5 rounded border border-slate-200"
            />
          </label>
          <label className="block text-sm">
            <span className="block text-xs text-slate-600 mb-1">{t("classes.addStudents.createNameEn")}</span>
            <input
              type="text"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              placeholder={t("classes.addStudents.createNameEnPlaceholder")}
              className="w-full px-2 py-1.5 rounded border border-slate-200"
            />
          </label>
        </div>

        <label className="block text-sm">
          <span className="block text-xs text-slate-600 mb-1">{t("classes.addStudents.createCode")}</span>
          <input
            type="text"
            value={studentCode}
            onChange={(e) => setStudentCode(e.target.value)}
            placeholder={t("classes.addStudents.createCodePlaceholder")}
            className="w-full px-2 py-1.5 rounded border border-slate-200"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="block text-xs text-slate-600 mb-1">
              {t("classes.addStudents.createGrade")} <span className="text-red-500">*</span>
            </span>
            <input
              type="text"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              placeholder="G7"
              className="w-full px-2 py-1.5 rounded border border-slate-200"
            />
          </label>
          <label className="block text-sm">
            <span className="block text-xs text-slate-600 mb-1">{t("classes.addStudents.createSection")}</span>
            <input
              type="text"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              placeholder="A"
              className="w-full px-2 py-1.5 rounded border border-slate-200"
            />
          </label>
        </div>

        {err && <div className="text-xs text-red-600">{err}</div>}
      </div>

      <footer className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-3 py-1.5 text-sm text-slate-600 rounded hover:bg-slate-100 disabled:opacity-50"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !nameZh.trim()}
          className="px-4 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
        >
          {busy ? t("classes.addStudents.submitting") : t("classes.addStudents.createSubmit")}
        </button>
      </footer>
    </>
  );
}
